<script setup>
import { computed, ref } from 'vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import Button from 'next/button/Button.vue';
import Input from 'next/input/Input.vue';

const props = defineProps({
  inboxId: { type: [Number, String], required: true },
  phone: { type: String, default: '' },
  status: { type: String, default: '' },
  coexistence: { type: Boolean, default: false },
});
const emit = defineEmits(['verified']);
const copy = {
  success: 'Número verificado pela Meta.',
  sent: 'SMS solicitado. Aguarde o recebimento; para reenviar, espere pelo menos um minuto.',
};
const description = computed(
  () =>
    `Solicite um código por SMS para ${props.phone} e informe abaixo o código recebido.`
);
const { isAdmin } = useAdmin();
const opened = ref(false);
const busy = ref(false);
const sent = ref(false);
const verified = ref(false);
const code = ref('');
const error = ref('');
const visible = computed(
  () => isAdmin.value && !props.coexistence && props.status !== 'VERIFIED'
);

const requestCode = async () => {
  busy.value = true;
  error.value = '';
  try {
    await InboxHealthAPI.requestPhoneVerification(props.inboxId);
    sent.value = true;
  } catch (e) {
    error.value =
      e.response?.data?.error ||
      'Não foi possível solicitar o SMS. Tente novamente.';
  } finally {
    busy.value = false;
  }
};
const verifyCode = async () => {
  busy.value = true;
  error.value = '';
  try {
    await InboxHealthAPI.verifyPhoneCode(props.inboxId, code.value);
    code.value = '';
    verified.value = true;
    emit('verified');
  } catch (e) {
    error.value =
      e.response?.data?.error ||
      'Não foi possível confirmar o código. Tente novamente.';
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <div v-if="visible || verified" class="w-full mt-2">
    <p v-if="verified" role="status" class="text-n-teal-11">
      {{ copy.success }}
    </p>
    <Button
      v-else-if="!opened"
      label="Verificar por SMS"
      sm
      @click="opened = true"
    />
    <div v-else class="flex flex-col gap-3 rounded-lg border border-n-weak p-3">
      <p class="text-sm text-n-slate-11">
        {{ description }}
      </p>
      <p v-if="sent" role="status" class="text-sm">
        {{ copy.sent }}
      </p>
      <p v-if="error" role="alert" class="text-sm text-n-ruby-11">
        {{ error }}
      </p>
      <Button
        :label="sent ? 'Reenviar SMS' : 'Enviar código por SMS'"
        :disabled="busy"
        sm
        @click="requestCode"
      />
      <form class="flex flex-col gap-3" @submit.prevent="verifyCode">
        <Input
          v-model="code"
          label="Código recebido por SMS"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="6"
          :disabled="busy"
        />
        <div class="flex gap-2">
          <Button
            type="submit"
            label="Confirmar código"
            :disabled="busy || !/^\d{6}$/.test(code)"
            sm
          />
          <Button
            type="button"
            label="Cancelar"
            variant="faded"
            :disabled="busy"
            sm
            @click="
              opened = false;
              code = '';
              error = '';
            "
          />
        </div>
      </form>
    </div>
  </div>
</template>
